import React, { FC } from 'react';
import { Paragraph2, Paragraph4 } from '@nexus/lib-react/dist/core/Typography/Paragraph';
import ButtonTertiaryDark from '@nexus/lib-react/dist/core/Button/ButtonTertiaryDark';
import useI18n from '../../../../i18n/hooks/useI18n';
import { ReturnDeliveryInfo } from '../../../../../typings/returns';

import S from './style/SellerReturnMethodDetails';
import { EXTERNAL_LINKS } from '../../constants';

interface ReturnMethodDetailsProps {
  isSeller: boolean;
  deliveryDetails: ReturnDeliveryInfo;
}

const ReturnMethodDetails: FC<ReturnMethodDetailsProps> = ({ isSeller, deliveryDetails }) => {
  const i18n = useI18n();
  const { product, parcel, location, tracking, posting } = deliveryDetails;
  const isD2D = product.deliveryMethod === 'DOOR_TO_DOOR';
  const address = location?.address ? location.address.split(/\r?\n/) : '';
  const packageSizeInfo = `${i18n('dlv_returns_details_return_method_package_size')}: ${
    parcel ? parcel.label : ''
  }`;
  const postingWindowStart = posting?.window?.start;
  const postingWindowEnd = posting?.window?.end;
  const addLeadingZero = (value: number) => (value.toString().length === 1 ? `0${value}` : value);

  const postingWindowStartDate = new Date(postingWindowStart);
  const day = addLeadingZero(postingWindowStartDate.getDay());
  const month = addLeadingZero(postingWindowStartDate.getMonth());
  const year = postingWindowStartDate.getFullYear().toString();
  const hour = () => addLeadingZero(postingWindowStartDate.getHours());
  const minutes = () => addLeadingZero(postingWindowStartDate.getMinutes());
  const startTime = `${hour()}:${minutes()}`;

  const postingDeadlineEndDate = new Date(postingWindowEnd);
  const endHour = () => addLeadingZero(postingDeadlineEndDate.getHours());
  const endMinutes = () => addLeadingZero(postingDeadlineEndDate.getMinutes());
  const endTime = `${endHour()}:${endMinutes()}`;

  const postingWindow = `${day}.${month}.${year}, ${startTime} - ${endTime}`;

  const waitingForNumberKey = isSeller
    ? 'dlv_returns_details_return_method_waiting_for_tracking_seller'
    : 'dlv_returns_details_return_method_waiting_for_tracking';

  return (
    <S.ReturnMethodContainer data-cy="returns-details-return-method">
      <S.DropOffPointDetails data-testid="returns-details-drop-point">
        {location.address ? (
          <>
            <Paragraph2>{location.name}</Paragraph2>
            <S.PackageSizeBlock designForD2D={isD2D}>
              {isD2D && isSeller && (
                <S.PickupTitle>{i18n`dlv_returns_details_delivery_address`}</S.PickupTitle>
              )}
              {isD2D && !isSeller && (
                <S.PickupTitle>{i18n`dlv_returns_details_pickup_address`}</S.PickupTitle>
              )}
              <div data-testid="returns-details-provider-address">
                <S.NormalP3>{address[0]}</S.NormalP3>
                <S.NormalP3>{address[1]}</S.NormalP3>
              </div>
              {isD2D && !isSeller && (
                <>
                  <S.PickupTitle>{i18n`dlv_return_details_pickup_date`}</S.PickupTitle>
                  {isD2D && posting?.window ? (
                    <>
                      <Paragraph4>{postingWindow}</Paragraph4>
                      <S.LightP4>
                        {i18n`dlv_returns_details_pickup_not_home_info`}{' '}
                        <a
                          href={EXTERNAL_LINKS.PICKUP_POINT_URL}
                          target="_blank"
                          rel="noreferrer"
                        >{i18n`dlv_returns_details_pickup_not_home_info_link`}</a>
                      </S.LightP4>
                    </>
                  ) : (
                    <Paragraph4>{i18n`dlv_return_details_pickup_date_info`}</Paragraph4>
                  )}
                </>
              )}
              {!isD2D && <S.PackagePointDetail>{location.description}</S.PackagePointDetail>}
            </S.PackageSizeBlock>
          </>
        ) : (
          <S.ProviderName data-testid="returns-details-provider-label">
            {product.title}
          </S.ProviderName>
        )}
        <S.PackageSizeBlock designForD2D={false}>
          {!isD2D && <S.PackageSizeBlockHeader>{packageSizeInfo}</S.PackageSizeBlockHeader>}
          <S.NormalP3>{`max. ${parcel.maxValue}`}</S.NormalP3>
          <S.LightP4>{i18n`dlv_returns_details_package_info`}</S.LightP4>
        </S.PackageSizeBlock>
      </S.DropOffPointDetails>
      <S.BlockHeader data-testid="returns-details-tracking">
        <Paragraph2>{i18n`dlv_returns_details_return_method_tracking_number`}</Paragraph2>
      </S.BlockHeader>
      {tracking ? (
        <>
          <S.TrackingNumberContainer>
            <S.TruckIcon />
            <S.LinkToTrack
              href={tracking.url}
              underlined
              data-testid="returns-details-tracking-id"
              data-cy="returns-details-tracking-id"
            >
              {tracking.id}
            </S.LinkToTrack>
          </S.TrackingNumberContainer>
          {posting && (
            <ButtonTertiaryDark
              as="a"
              rel="noopener noreferrer"
              target="_blank"
              href={posting.labelUrl}
              data-testid="returns-details-download-label"
              data-cy="returns-details-download-label"
            >
              <S.DownloadLabelParagraph>
                {i18n`dlv_returns_details_return_method_download_label`}
              </S.DownloadLabelParagraph>
            </ButtonTertiaryDark>
          )}
        </>
      ) : (
        <Paragraph4>{i18n(waitingForNumberKey)}</Paragraph4>
      )}
    </S.ReturnMethodContainer>
  );
};

export default ReturnMethodDetails;
